import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import { AlertSnippetDto, CustomerDto, CustomerType, EventDto } from '../../interfaces/IEvent'
import { AlertDto, NotifiedCustomers } from '../../interfaces/IAlert'
import { PhoneOutlined, SearchOutlined } from '@mui/icons-material'
import { ReactNode, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { AlertSelectionCards } from '../../interfaces/IFields'
import { toDateTime } from '../../utils/ValueFormatters'
import { buildAlertFilterOptions, filterCustomersByAlerts, FilterMode } from './customerAlertHistoryFilter'
import StyledToggleButtonGroup from '../../components/InputFields/StyledToggleButtonGroup'
import { buildFullAlertHistory } from './customerAlertHistory'

interface AlertCustomerSelectionCardsProps {
  viewEvent: EventDto
  currentAlert: AlertDto
  onSelectedCustomersChange: (selectedCustomers: NotifiedCustomers) => void
  customerAlertHistory?: Map<string, AlertSnippetDto[]>
  customerLanguages: Record<string, string>
  onSetCustomerLanguage: (customerNumber: string, language: string) => void
}

interface SelectionCard {
  icon: ReactNode
  id: string
  name: string
  data: CustomerDto[] | string[]
  selected: boolean
  disabled?: boolean
}

/** Returns selected count label: "Alle (N)", "X / N", or "0" */
function groupLabel(card: SelectionCard, selectedIds: string[]): string {
  const total = card.data.length
  const selected = selectedIds?.length ?? 0
  if (selected === total) return `Alle (${total})`
  return `${selected} / ${total}`
}

/** Applies the alert-history filter — returns the input unchanged if no filter is active. */
function applyAlertFilter<T>(items: T[], getId: (item: T) => string, filteredIds: Set<string> | null): T[] {
  if (!filteredIds) return items
  return items.filter((i) => filteredIds.has(getId(i)))
}

function AlertCustomerSelectionCards({
  viewEvent,
  currentAlert,
  onSelectedCustomersChange,
  customerAlertHistory,
  customerLanguages,
  onSetCustomerLanguage,
}: AlertCustomerSelectionCardsProps) {
  const theme = useTheme()
  const allCompanyCustomersSelectedForAlert = currentAlert.notifiedCustomers.allCompanyCustomers

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [selectedFilterAlertIds, setSelectedFilterAlertIds] = useState<string[]>([])
  const [filterMode, setFilterMode] = useState<FilterMode>('notReceived')
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({})
  const deferredSearchTerms = useDeferredValue(searchTerms)
  const [currentPages, setCurrentPages] = useState<Record<string, number>>({})
  const PAGE_SIZE = 10

  const alertFilterOptions = useMemo(() => buildAlertFilterOptions(viewEvent.alerts), [viewEvent.alerts])

  const allCustomerIds = useMemo(() => {
    const ids: string[] = []
    viewEvent.affectedCustomers?.forEach((c) => ids.push(c.customerNumber))
    viewEvent.affectedPhoneNumbers?.forEach((p) => ids.push(p))
    viewEvent.affectedEmailAddresses?.forEach((e) => ids.push(e))
    return ids
  }, [viewEvent.affectedCustomers, viewEvent.affectedPhoneNumbers, viewEvent.affectedEmailAddresses])

  const fullAlertHistory = useMemo(() => buildFullAlertHistory(viewEvent.alerts), [viewEvent.alerts])

  const filteredCustomerIds = useMemo(() => {
    if (selectedFilterAlertIds.length === 0) return null
    return filterCustomersByAlerts(fullAlertHistory, selectedFilterAlertIds, filterMode, allCustomerIds)
  }, [fullAlertHistory, selectedFilterAlertIds, filterMode, allCustomerIds])

  const hasActiveFilter = selectedFilterAlertIds.length > 0

  const affected: SelectionCard[] = [
    {
      icon: <BusinessOutlinedIcon />,
      id: AlertSelectionCards.companyCustomerIds,
      name: 'Bedrift',
      data: viewEvent.affectedCustomers?.filter((x) => x.customerType === CustomerType.Company),
      selected: currentAlert.notifiedCustomers.companyCustomerIds?.length > 0,
    },
    {
      icon: <KeyOutlinedIcon />,
      id: AlertSelectionCards.companyWithSlaCustomerIds,
      name: 'SLA',
      data: viewEvent.affectedCustomers?.filter((x) => x.customerType === CustomerType.CompanyWithSla),
      selected: currentAlert.notifiedCustomers.companyWithSlaCustomerIds?.length > 0,
    },
    {
      icon: <PhoneOutlined />,
      id: AlertSelectionCards.phoneNumbers,
      name: 'Telefon',
      data: viewEvent.affectedPhoneNumbers,
      selected: currentAlert.phoneNumbers?.length > 0,
    },
    {
      icon: <EmailOutlined />,
      id: AlertSelectionCards.emailAddresses,
      name: 'Epost',
      data: viewEvent.affectedEmailAddresses,
      selected: currentAlert.emailAddresses?.length > 0,
    },
  ]

  const isPhoneNumberSelected = affected.find((x) => x.id === AlertSelectionCards.phoneNumbers)?.selected
  const isEmailAddressSelected = affected.find((x) => x.id === AlertSelectionCards.emailAddresses)?.selected
  const isAnyOtherThanPhoneOrEmailSelected = affected
    .filter((x) => x.id !== AlertSelectionCards.phoneNumbers && x.id !== AlertSelectionCards.emailAddresses)
    .some((x) => x.selected)

  affected.forEach((card) => {
    if (card.id === AlertSelectionCards.companyCustomerIds)
      card.disabled = (isPhoneNumberSelected || isEmailAddressSelected || allCompanyCustomersSelectedForAlert) && !card.selected
    if (card.id === AlertSelectionCards.companyWithSlaCustomerIds)
      card.disabled = (isPhoneNumberSelected || isEmailAddressSelected || allCompanyCustomersSelectedForAlert) && !card.selected
    if (card.id === AlertSelectionCards.phoneNumbers) card.disabled = (isAnyOtherThanPhoneOrEmailSelected || isEmailAddressSelected) && !card.selected
    if (card.id === AlertSelectionCards.emailAddresses)
      card.disabled = (isAnyOtherThanPhoneOrEmailSelected || isPhoneNumberSelected || allCompanyCustomersSelectedForAlert) && !card.selected
  })

  useEffect(() => {
    setExpandedGroups((prev) => {
      let changed = false
      const next = new Set(prev)
      affected.forEach((card) => {
        if (card.disabled && next.has(card.id)) {
          next.delete(card.id)
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [currentAlert.notifiedCustomers, currentAlert.phoneNumbers, currentAlert.emailAddresses])

  useEffect(() => {
    setCurrentPages({})
  }, [selectedFilterAlertIds, filterMode])

  /** Toggle all customers in a group on/off */
  const handleAffectedCardSelection = (card: SelectionCard) => {
    if (card) {
      const newData = card.selected ? [] : card.data.map((x) => (typeof x === 'string' ? x : x.customerNumber))
      const updatedCustomers = {
        ...currentAlert.notifiedCustomers,
        [card.id]: newData,
      }
      onSelectedCustomersChange(updatedCustomers)
    }
  }

  /** Toggle a single item within a group */
  const handleIndividualCustomerToggle = (cardId: string, value: string) => {
    const isPhoneOrEmail = cardId === AlertSelectionCards.phoneNumbers || cardId === AlertSelectionCards.emailAddresses
    const currentIds: string[] = isPhoneOrEmail ? ((currentAlert as any)[cardId] ?? []) : ((currentAlert.notifiedCustomers as any)[cardId] ?? [])
    const isAlreadySelected = currentIds.includes(value)
    const newIds = isAlreadySelected ? currentIds.filter((id) => id !== value) : [...currentIds, value]
    onSelectedCustomersChange({
      ...currentAlert.notifiedCustomers,
      [cardId]: newIds,
    })
  }

  const handleAllCompanyCustomersSelected = () => {
    const updatedCustomers = {
      ...currentAlert.notifiedCustomers,
      phoneNumbers: currentAlert.phoneNumbers,
      allCompanyCustomers: !currentAlert.notifiedCustomers.allCompanyCustomers,
    }
    onSelectedCustomersChange(updatedCustomers)
  }

  const toggleExpand = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(cardId)) next.delete(cardId)
      else next.add(cardId)
      return next
    })
  }

  /** Render an expandable card with individual customer checkboxes */
  const renderCustomerGroupCard = (card: SelectionCard) => {
    if (!card.data || card.data.length === 0) return null
    const customers = card.data as CustomerDto[]
    const selectedIds: string[] = (currentAlert.notifiedCustomers as any)[card.id] ?? []
    const isExpanded = expandedGroups.has(card.id)
    const label = groupLabel(card, selectedIds)
    const search = (deferredSearchTerms[card.id] ?? '').toLowerCase()
    const page = currentPages[card.id] ?? 0

    const searchedCustomers = search
      ? customers.filter((c) => {
          const addresses = c.affectedAddresses ?? []
          return (
            c.customerName?.toLowerCase().includes(search) ||
            c.customerNumber?.toLowerCase().includes(search) ||
            addresses.some((a) => a.servicePointId?.toLowerCase().includes(search)) ||
            addresses.some((a) => a.connectionNumbers?.some((cn) => cn?.toLowerCase().includes(search)))
          )
        })
      : customers

    const visibleCustomers = applyAlertFilter(searchedCustomers, (c) => c.customerNumber, filteredCustomerIds)
    const totalPages = Math.ceil(visibleCustomers.length / PAGE_SIZE)
    const paginatedCustomers = visibleCustomers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    const filterMatchCount = filteredCustomerIds
      ? applyAlertFilter(customers, (c) => c.customerNumber, filteredCustomerIds).length
      : 0

    return (
      <Grid
        key={card.id}
        size={{
          xs: 12,
          md: 12,
        }}
        sx={{ mb: 1 }}
      >
        <Box
          sx={{
            border: 2,
            borderRadius: '8px',
            borderColor: card.disabled ? 'background.paper' : card.selected ? theme.palette.primary.main : theme.palette.primary.light,
            bgcolor: card.disabled ? 'background.paper' : `rgba(${theme.palette.primary.light}, 0.25)`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              cursor: card.disabled ? 'not-allowed' : 'pointer',
              bgcolor: card.selected ? `${theme.palette.primary.light}66` : 'transparent',
            }}
            onClick={card.disabled ? undefined : () => handleAffectedCardSelection(card)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: card.disabled ? 'text.disabled' : 'inherit', mr: 1 }}>{card.icon}</Box>
            <Typography variant="subtitle1" sx={{ flexGrow: 1, color: card.disabled ? 'text.disabled' : 'text.primary' }}>
              {card.name}
            </Typography>
            <Box sx={{ width: 90, display: 'flex', justifyContent: 'flex-end', mr: 1 }}>
              {hasActiveFilter && filteredCustomerIds && (
                <Chip
                  size="small"
                  label={`${filterMatchCount} treff`}
                  color={filterMatchCount > 0 ? 'primary' : 'default'}
                  variant={filterMatchCount > 0 ? 'filled' : 'outlined'}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ mr: 1, minWidth: 70, textAlign: 'right', color: card.disabled ? 'text.disabled' : 'text.secondary' }}>
              {label}
            </Typography>
            <IconButton size="small" onClick={(e) => toggleExpand(card.id, e)} disabled={card.disabled}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded}>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 0.5 }}>
              {!hasActiveFilter && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: customers.map((c) => c.customerNumber),
                    })
                  }
                >
                  Velg alle
                </Typography>
              )}
              {hasActiveFilter && filteredCustomerIds && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: customers.filter((c) => filteredCustomerIds.has(c.customerNumber)).map((c) => c.customerNumber),
                    })
                  }
                >
                  Velg filtrerte
                </Typography>
              )}
              {!hasActiveFilter && customerAlertHistory && customerAlertHistory.size > 0 && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: customers.filter((c) => !customerAlertHistory.has(c.customerNumber)).map((c) => c.customerNumber),
                    })
                  }
                >
                  Velg alle uvarslet
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                onClick={() =>
                  onSelectedCustomersChange({
                    ...currentAlert.notifiedCustomers,
                    [card.id]: [],
                  })
                }
              >
                Fjern alle
              </Typography>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Søk etter navn, sambandsnummer, leveransepunktid..."
              value={searchTerms[card.id] ?? ''}
              onChange={(e) => {
                setSearchTerms((prev) => ({ ...prev, [card.id]: e.target.value }))
                setCurrentPages((prev) => ({ ...prev, [card.id]: 0 }))
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{ px: 2, pt: 1, pb: 0.5 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> } }}
            />
            <List dense disablePadding>
              {paginatedCustomers.length === 0 && (search || hasActiveFilter) && (
                <ListItem><ListItemText primary="Ingen treff" slotProps={{ primary: { variant: 'body2', color: 'text.secondary', sx: { py: 1, textAlign: 'center' } } }} /></ListItem>
              )}
              {paginatedCustomers.map((customer) => {
                const isSelected = selectedIds.includes(customer.customerNumber)
                const priorAlerts = customerAlertHistory?.get(customer.customerNumber) ?? []
                const priorCount = priorAlerts.length
                const isEnglish = (customerLanguages[customer.customerNumber] ?? 'Norwegian') === 'English'
                return (
                  <ListItem
                    key={customer.customerNumber}
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={isEnglish ? 'Kundespråk: Engelsk (klikk for norsk)' : 'Kundespråk: Norsk (klikk for engelsk)'}>
                          <Chip
                            size="small"
                            label={isEnglish ? 'EN' : 'NO'}
                            color={isEnglish ? 'primary' : 'default'}
                            variant={isEnglish ? 'filled' : 'outlined'}
                            onClick={(e) => {
                              e.stopPropagation()
                              onSetCustomerLanguage(customer.customerNumber, isEnglish ? 'Norwegian' : 'English')
                            }}
                            sx={{ minWidth: 44 }}
                          />
                        </Tooltip>
                        {priorCount > 0 && (
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                  Tidligere varsler ({priorCount}):
                                </Typography>
                                {priorAlerts.map((a) => (
                                  <Typography key={a.id} variant="caption" sx={{ display: 'block' }}>
                                    • {a.description || '(uten beskrivelse)'} — {toDateTime(new Date(a.sendOn))}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <Badge badgeContent={priorCount} color="primary" invisible={priorCount <= 1}>
                              <NotificationsOutlinedIcon fontSize="small" color="action" />
                            </Badge>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton dense onClick={() => handleIndividualCustomerToggle(card.id, customer.customerNumber)} disabled={card.disabled} sx={{ minWidth: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={isSelected} size="small" disableRipple />
                      </ListItemIcon>
                      {(() => {
                        const servicePointIds = (customer.affectedAddresses ?? []).map((a) => a.servicePointId).filter(Boolean)
                        const connectionNumbers = (customer.affectedAddresses ?? []).flatMap((a) => a.connectionNumbers ?? []).filter(Boolean)
                        const secondaryTitle = [
                          servicePointIds.length > 0 ? `LP: ${servicePointIds.join(', ')}` : '',
                          connectionNumbers.length > 0 ? `Samband: ${connectionNumbers.join(', ')}` : '',
                        ].filter(Boolean).join(' | ')
                        return (
                          <ListItemText
                            sx={{ minWidth: 0, pr: 11 }}
                            primary={customer.customerName}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }} title={secondaryTitle}>
                                {servicePointIds.length > 0 && (
                                  <Box component="span">
                                    <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>LP:</Box>
                                    {servicePointIds.join(', ')}
                                  </Box>
                                )}
                                {connectionNumbers.length > 0 && (
                                  <Box component="span">
                                    <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>Samband:</Box>
                                    {connectionNumbers.join(', ')}
                                  </Box>
                                )}
                              </Box>
                            }
                            slotProps={{
                              primary: { variant: 'body2' },
                              secondary: { variant: 'caption', component: 'div' },
                            }}
                          />
                        )
                      })()}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, searchedCustomers.length)} av {searchedCustomers.length}
                </Typography>
                <Pagination
                  size="small"
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setCurrentPages((prev) => ({ ...prev, [card.id]: p - 1 }))}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      </Grid>
    )
  }

  /** Render an expandable card for string-based groups (phone numbers, email addresses) */
  const renderStringGroupCard = (card: SelectionCard) => {
    if (!card.data || card.data.length === 0) return null
    const items = card.data as string[]
    const selectedIds: string[] = (currentAlert as any)[card.id] ?? []
    const isExpanded = expandedGroups.has(card.id)
    const label = groupLabel(card, selectedIds)
    const search = (deferredSearchTerms[card.id] ?? '').toLowerCase()
    const page = currentPages[card.id] ?? 0

    const searchedItems = search ? items.filter((i) => i.toLowerCase().includes(search)) : items
    const visibleItems = applyAlertFilter(searchedItems, (i) => i, filteredCustomerIds)
    const totalPages = Math.ceil(visibleItems.length / PAGE_SIZE)
    const paginatedItems = visibleItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    const filterMatchCount = filteredCustomerIds
      ? applyAlertFilter(items, (i) => i, filteredCustomerIds).length
      : 0

    return (
      <Grid
        key={card.id}
        size={{
          xs: 12,
          md: 12,
        }}
        sx={{ mb: 1 }}
      >
        <Box
          sx={{
            border: 2,
            borderRadius: '8px',
            borderColor: card.disabled ? 'background.paper' : card.selected ? theme.palette.primary.main : theme.palette.primary.light,
            bgcolor: card.disabled ? 'background.paper' : `rgba(${theme.palette.primary.light}, 0.25)`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              cursor: card.disabled ? 'not-allowed' : 'pointer',
              bgcolor: card.selected ? `${theme.palette.primary.light}66` : 'transparent',
            }}
            onClick={card.disabled ? undefined : () => handleAffectedCardSelection(card)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: card.disabled ? 'text.disabled' : 'inherit', mr: 1 }}>{card.icon}</Box>
            <Typography variant="subtitle1" sx={{ flexGrow: 1, color: card.disabled ? 'text.disabled' : 'text.primary' }}>
              {card.name}
            </Typography>
            <Box sx={{ width: 90, display: 'flex', justifyContent: 'flex-end', mr: 1 }}>
              {hasActiveFilter && filteredCustomerIds && (
                <Chip
                  size="small"
                  label={`${filterMatchCount} treff`}
                  color={filterMatchCount > 0 ? 'primary' : 'default'}
                  variant={filterMatchCount > 0 ? 'filled' : 'outlined'}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ mr: 1, minWidth: 70, textAlign: 'right', color: card.disabled ? 'text.disabled' : 'text.secondary' }}>
              {label}
            </Typography>
            <IconButton size="small" onClick={(e) => toggleExpand(card.id, e)} disabled={card.disabled}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded}>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 0.5 }}>
              {!hasActiveFilter && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: [...items],
                    })
                  }
                >
                  Velg alle
                </Typography>
              )}
              {hasActiveFilter && filteredCustomerIds && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: items.filter((i) => filteredCustomerIds.has(i)),
                    })
                  }
                >
                  Velg filtrerte
                </Typography>
              )}
              {!hasActiveFilter && customerAlertHistory && customerAlertHistory.size > 0 && (
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main', mr: 2 }}
                  onClick={() =>
                    onSelectedCustomersChange({
                      ...currentAlert.notifiedCustomers,
                      [card.id]: items.filter((i) => !customerAlertHistory.has(i)),
                    })
                  }
                >
                  Velg alle uvarslet
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                onClick={() =>
                  onSelectedCustomersChange({
                    ...currentAlert.notifiedCustomers,
                    [card.id]: [],
                  })
                }
              >
                Fjern alle
              </Typography>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Søk..."
              value={searchTerms[card.id] ?? ''}
              onChange={(e) => {
                setSearchTerms((prev) => ({ ...prev, [card.id]: e.target.value }))
                setCurrentPages((prev) => ({ ...prev, [card.id]: 0 }))
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{ px: 2, pt: 1, pb: 0.5 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> } }}
            />
            <List dense disablePadding>
              {paginatedItems.length === 0 && (search || hasActiveFilter) && (
                <ListItem><ListItemText primary="Ingen treff" slotProps={{ primary: { variant: 'body2', color: 'text.secondary', sx: { py: 1, textAlign: 'center' } } }} /></ListItem>
              )}
              {paginatedItems.map((item) => {
                const isSelected = selectedIds.includes(item)
                const priorAlerts = customerAlertHistory?.get(item) ?? []
                const priorCount = priorAlerts.length
                return (
                  <ListItem
                    key={item}
                    disablePadding
                    secondaryAction={
                      priorCount > 0 ? (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                Tidligere varsler ({priorCount}):
                              </Typography>
                              {priorAlerts.map((a) => (
                                <Typography key={a.id} variant="caption" sx={{ display: 'block' }}>
                                  • {a.description || '(uten beskrivelse)'} — {toDateTime(new Date(a.sendOn))}
                                </Typography>
                              ))}
                            </Box>
                          }
                        >
                          <Badge badgeContent={priorCount} color="primary" invisible={priorCount <= 1}>
                            <NotificationsOutlinedIcon fontSize="small" color="action" />
                          </Badge>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemButton dense onClick={() => handleIndividualCustomerToggle(card.id, item)} disabled={card.disabled}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={isSelected} size="small" disableRipple />
                      </ListItemIcon>
                      <ListItemText primary={item} slotProps={{ primary: { variant: 'body2' } }} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, searchedItems.length)} av {searchedItems.length}
                </Typography>
                <Pagination
                  size="small"
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setCurrentPages((prev) => ({ ...prev, [card.id]: p - 1 }))}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      </Grid>
    )
  }

  return (
    <ClickAwayListener onClickAway={() => setExpandedGroups(new Set())}>
      <Box>
      {alertFilterOptions.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Filtrer etter varsel</InputLabel>
            <Select<string[]>
              multiple
              value={selectedFilterAlertIds}
              onChange={(event) => {
                const value = event.target.value
                setSelectedFilterAlertIds(typeof value === 'string' ? value.split(',') : value)
              }}
              input={<OutlinedInput label="Filtrer etter varsel" />}
              MenuProps={{ disablePortal: true }}
              renderValue={(selected) => {
                const ids = selected
                const visible = ids.slice(0, 2)
                const overflow = ids.length - visible.length
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                    {visible.map((id) => {
                      const optionLabel = alertFilterOptions.find((o) => o.id === id)?.label ?? id
                      return <Chip key={id} size="small" label={optionLabel} sx={{ maxWidth: 140 }} />
                    })}
                    {overflow > 0 && <Chip size="small" label={`+${overflow}`} />}
                  </Box>
                )
              }}
            >
              {alertFilterOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selectedFilterAlertIds.includes(option.id)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilter && (
            <StyledToggleButtonGroup
              value={filterMode}
              onChange={setFilterMode}
              options={[
                { value: 'notReceived' as FilterMode, label: 'Ikke inkludert' },
                { value: 'received' as FilterMode, label: 'Inkludert' },
              ]}
            />
          )}
          {hasActiveFilter && (
            <Button
              size="small"
              onClick={() => {
                setSelectedFilterAlertIds([])
                setFilterMode('notReceived')
              }}
            >
              Nullstill filter
            </Button>
          )}
        </Box>
      )}
      {viewEvent.isAllCompanyCustomersAffected && (() => {
        const isDisabled = affected.some((x) => x.selected && x.id !== AlertSelectionCards.phoneNumbers)
        const isSelected = currentAlert.notifiedCustomers.allCompanyCustomers
        return (
          <Grid
            key="allCompanyCustomers"
            size={{
              xs: 12,
              md: 12,
            }}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                border: 2,
                borderRadius: '8px',
                borderColor: isDisabled ? 'background.paper' : isSelected ? theme.palette.primary.main : theme.palette.primary.light,
                bgcolor: isDisabled ? 'background.paper' : `rgba(${theme.palette.primary.light}, 0.25)`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  bgcolor: isSelected ? `${theme.palette.primary.light}66` : 'transparent',
                }}
                onClick={isDisabled ? undefined : handleAllCompanyCustomersSelected}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', color: isDisabled ? 'text.disabled' : 'inherit', mr: 1 }}>
                  <BusinessOutlinedIcon />
                </Box>
                <Typography variant="subtitle1" sx={{ flexGrow: 1, color: isDisabled ? 'text.disabled' : 'text.primary' }}>
                  Alle bedriftskunder
                </Typography>
                <Typography variant="body2" sx={{ color: isDisabled ? 'text.disabled' : 'text.secondary' }}>
                  Alle
                </Typography>
              </Box>
            </Box>
          </Grid>
        )
      })()}
      {/* Customer group cards with expandable individual selection */}
      {affected
        .filter((card) => card.id !== AlertSelectionCards.phoneNumbers && card.id !== AlertSelectionCards.emailAddresses && card.data?.length > 0)
        .map((card) => renderCustomerGroupCard(card))}
      {/* Phone and email cards with expandable individual selection */}
      {affected
        .filter((card) => (card.id === AlertSelectionCards.phoneNumbers || card.id === AlertSelectionCards.emailAddresses) && card.data?.length > 0)
        .map((card) => renderStringGroupCard(card))}
      </Box>
    </ClickAwayListener>
  )
}

export default AlertCustomerSelectionCards
